//* seller용 배송상태 관리 카드
import { DragEvent, useRef, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { useDeliveredImageUpload } from '@service/seller/hooks/useDeliveredImageUpload';
import { ShopOrderForSeller } from '@shared/api/shopOrder/shopOrder.interface';
import {
  feventAddressMismatch,
  orderContentString as shopOrderForSellerContentString,
} from '@shared/api/shopOrder/shopOrder.utils';
import { Button } from '@shared/components/Button';
import { Icon } from '@shared/components/icons';
import { DeliveredImageUploader } from '@shared/components/ImageUploader';
import { DeliveryProgressIndicator } from '@shared/containers/DeliveryProgressIndicator/DeliveryProgressIndicator';
import { DeathCharacter } from '@shared/miscValues';
import { notificateSuccess } from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { DeliveryState, LowerRole, PaymentState } from '@shared/types';
import {
  fileBucketUrl,
  readFileAsDataUrl,
  validateImageFile,
} from '@shared/utils/fileUtils';
import { formatDate } from '@shared/utils/formatDate';
import { bareReceiverName } from '@shared/utils/receiverName.util';
import { Image, Tag } from 'antd';

import { ShopOrderCsMemoBlock } from './csMemo';
import { DeliveryContentCard } from './DeliveryContentCard';
import { DeliveryStatusManageButton } from './DeliveryStatusManageButton';
import { OrderAddressEditModal } from './OrderAddressEditModal';
import { OrderBugoDetailEditModal } from './OrderBugoDetailEditModal';
import { OrderItemChangeModal } from './OrderItemChangeModal';
import { ShopOrderDetailInfoCard } from './ShopOrderDetailInfoCard';

interface DeliveryManageCardProps {
  order: ShopOrderForSeller;
}

export const DeliveryManageCard = function DeliveryManageCard({
  order,
}: DeliveryManageCardProps) {
  const [detailOpen, setDetailOpen] = useState(false);
  const [contentOpen, setContentOpen] = useState(false);
  const deliveryDetail = order.deliveryDetail;
  const shopItem = order.orderDetail.shopItem;
  const openId = `deliveryManageCard-${order._id}`;
  const setDeliveredImageUpload = useSetRecoilState(simpleOpenAtomFamily(openId));
  const newTag = (order: ShopOrderForSeller) => {
    if (
      order.deliveryDetail.status === DeliveryState.BEFORE_PREPARING &&
      [PaymentState.PAID, PaymentState.CONFIRMED].includes(order.paymentDetail.status)
    ) {
      return <Tag className="filled-warn border-0 py-1 font-bold opacity-80">NEW</Tag>;
    }
  };

  const agencyTag = (order: ShopOrderForSeller) => {
    return (
      <Tag className="filled-gray-800 border-0 py-1 font-bold opacity-80">
        {order.bugo?.bugoBrand?.bugoAgency?.nickName}
      </Tag>
    );
  };

  const editOpenId = `deliveryManageCard-edit-phrase-${order._id}`;
  const setEditOpen = useSetRecoilState(simpleOpenAtomFamily(editOpenId));

  //* 배송지 주소 수정 (호실 이동 등) — 주소 클릭 → 모달
  const addressEditOpenId = `deliveryManageCard-edit-address-${order._id}`;
  const setAddressEditOpen = useSetRecoilState(simpleOpenAtomFamily(addressEditOpenId));
  //* 부고 기준 현재 상가 주소와 다르면 빨간 (!) 표기 — 호실이동 감지
  const addressMismatch = feventAddressMismatch(order);

  //* admin 전용: 상품명 클릭 → 주문 상품 교체 (차액 수취 정정 처리)
  const { sellerRole } = useAuth();
  const isAdminUser = sellerRole() === LowerRole.Admin;
  const itemChangeOpenId = `deliveryManageCard-item-change-${order._id}`;
  const setItemChangeOpen = useSetRecoilState(simpleOpenAtomFamily(itemChangeOpenId));

  const { UpFromBracket, Copy } = Icon();

  //* 발주 시 개별 항목 클릭 복사 (더블클릭-드래그 복사 대체)
  const notificationInstance = useRecoilValue(notificationInstanceAtom);
  const copyText = async (label: string, text?: string) => {
    if (!text) return;
    await navigator.clipboard.writeText(text);
    notificateSuccess(notificationInstance, `${label} 복사 완료`);
  };

  //* receiverName은 생성 시 "OOO 상주님"으로 저장됨 — 접미사를 떼고 표시·복사 시 한 번만 붙인다
  const receiverNameBare = bareReceiverName(deliveryDetail.receiverName);

  //* 배송완료사진 퀵 드롭 업로드
  const { upload, isUploading } = useDeliveredImageUpload(order);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string>();
  const [isDragging, setIsDragging] = useState(false);
  const [zoomVisible, setZoomVisible] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setIsDragging(true);
  };
  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };
  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (!file || !validateImageFile(file)) return;
    setPendingFile(file);
    setPendingPreview(await readFileAsDataUrl(file));
  };

  const handleQuickSave = async () => {
    if (!pendingFile) return;
    const ok = await upload(pendingFile);
    if (ok) {
      setPendingFile(null);
      setPendingPreview(undefined);
    }
  };
  const handleQuickCancel = () => {
    setPendingFile(null);
    setPendingPreview(undefined);
  };

  const priceWhole = shopItem.shopItemInfo.priceWhole;
  return (
    <>
      <DeliveredImageUploader order={order} openId={openId} />
      <div
        key={order._id}
        className="relative min-w-[320px] font-sans"
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragging && (
          <div className="center-box pointer-events-none absolute inset-0 z-20 rounded-lg border-2 border-dashed border-cyan-500 bg-cyan-50/80">
            <div className="center-box gap-2 text-cyan-700">
              <UpFromBracket className="wh-6 fill-cyan-700" />
              <p className="text-base font-bold">여기에 놓으면 배송완료사진 업로드</p>
            </div>
          </div>
        )}
        <div className="absolute top-0 right-0">
          {agencyTag(order)} {newTag(order)}
        </div>
        <div className="h-auto w-full overflow-hidden rounded-lg border shadow">
          <div className="p-3 pb-2">
            <div className="flex items-center">
              <div className="w-full">
                <p
                  className={`flex-wrap truncate text-base font-medium text-gray-900 ${
                    isAdminUser
                      ? 'cursor-pointer underline decoration-dotted underline-offset-2 hover:text-cyan-700'
                      : ''
                  }`}
                  title={isAdminUser ? '클릭하여 상품 교체 (관리자)' : undefined}
                  onClick={
                    isAdminUser
                      ? () => {
                          setItemChangeOpen(true);
                        }
                      : undefined
                  }
                >
                  {shopItem.name ?? shopItem.shopItemInfo.name}
                </p>
                <div className="flex items-center justify-between">
                  <p className="flex-wrap truncate text-base font-medium text-gray-900">
                    {priceWhole.toLocaleString()}원{' '}
                    {order.paymentDetail.provider === 'Tosspayments' ? 'T ' : 'B '}
                    {order.paymentDetail.method
                      ? order.paymentDetail.method === '가상계좌'
                        ? '가상#'
                        : order.paymentDetail.method === '계좌 입금'
                        ? '입금#'
                        : order.paymentDetail.method.slice(0, 2)
                      : ''}
                    <span className="text-gray-500" title={`주문번호: ${order._id}`}>
                      {' ' + order._id.slice(-6)}
                    </span>
                  </p>
                  <div className="text-right">
                    <p
                      className="cursor-pointer text-base font-medium"
                      title="클릭하여 고인이름 복사"
                      onClick={() => {
                        const deceasedName = order.bugo?.fevent?.deceasedInfo?.name;
                        //* 복사는 표기와 동일하게 "故OOO님"으로
                        copyText(
                          '고인이름',
                          deceasedName ? `${DeathCharacter}${deceasedName}님` : undefined,
                        );
                      }}
                    >
                      {DeathCharacter}
                      {order.bugo?.fevent?.deceasedInfo?.name}님
                    </p>
                    {receiverNameBare && (
                      <p
                        className="cursor-pointer text-xs font-medium"
                        title="클릭하여 상주이름 복사"
                        onClick={() => {
                          copyText('상주이름', receiverNameBare);
                        }}
                      >
                        {receiverNameBare}
                      </p>
                    )}
                  </div>
                </div>
                <div className="mt-1">
                  <div className="flex items-start gap-1.5">
                    <button
                      type="button"
                      className="shrink-0 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs font-medium text-gray-600 shadow-sm hover:bg-gray-50"
                      title="클릭하여 주소 복사"
                      onClick={() => {
                        copyText('주소', deliveryDetail.receiverAddress);
                      }}
                    >
                      주소
                    </button>
                    <p
                      className={`cursor-pointer whitespace-pre-wrap break-keep text-sm text-gray-700 hover:text-cyan-700 ${
                        addressMismatch
                          ? 'underline decoration-dotted underline-offset-2'
                          : ''
                      }`}
                      title={
                        addressMismatch
                          ? '부고 기준 현재 상가 주소와 다릅니다 — 클릭하여 수정'
                          : '클릭하여 배송지 주소 수정'
                      }
                      onClick={() => {
                        setAddressEditOpen(true);
                      }}
                    >
                      {addressMismatch && (
                        <span className="font-bold text-red-500">(!) </span>
                      )}
                      {deliveryDetail.receiverAddress}
                    </p>
                  </div>
                  <div className="mt-1 space-y-0.5">
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        className="shrink-0 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs font-medium text-gray-600 shadow-sm hover:bg-gray-50"
                        title="클릭하여 보내는분 문구 복사"
                        onClick={() => {
                          copyText('보내는분', order.bugoDetail.senderPhrase);
                        }}
                      >
                        보내는분
                      </button>
                      <p
                        className="min-w-0 cursor-pointer truncate text-sm font-bold text-gray-700 hover:text-cyan-700"
                        title="클릭하여 문구 수정"
                        onClick={() => {
                          setEditOpen(true);
                        }}
                      >
                        {order.bugoDetail.senderPhrase}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        className="shrink-0 rounded border border-gray-300 bg-white px-1.5 py-0.5 text-xs font-medium text-gray-600 shadow-sm hover:bg-gray-50"
                        title="클릭하여 조의문구 복사"
                        onClick={() => {
                          copyText('조의문구', order.bugoDetail.condolencePhrase);
                        }}
                      >
                        조의문구
                      </button>
                      <p
                        className="min-w-0 cursor-pointer truncate text-sm font-bold text-gray-700 hover:text-cyan-700"
                        title="클릭하여 문구 수정"
                        onClick={() => {
                          setEditOpen(true);
                        }}
                      >
                        {order.bugoDetail.condolencePhrase}
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-1 whitespace-pre">
                    <p className="text-sm text-gray-700">
                      결제:{' '}
                      {formatDate(order.paymentDetail.purchasedAt, {
                        dateSeparater: '.',
                        contains: {
                          year: false,
                          day: true,
                          hours: true,
                          minutes: true,
                        },
                      })}
                    </p>
                    {order.deliveryDetail?.estimatedDeliveredAt && (
                      <p className="text-sm text-gray-700">
                        예상:{' '}
                        {formatDate(order.deliveryDetail.estimatedDeliveredAt, {
                          dateSeparater: '.',
                          contains: {
                            year: false,
                            day: true,
                            hours: true,
                            minutes: true,
                          },
                        })}
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-1 whitespace-pre">
                    <p className="text-sm text-gray-700">
                      입실:{' '}
                      {formatDate(order.bugo?.fevent?.deceasedInfo?.enterance?.date, {
                        dateSeparater: '.',
                        contains: {
                          year: false,
                          day: true,
                          hours: true,
                          minutes: true,
                        },
                      })}
                    </p>
                    <p className="text-sm text-gray-700">
                      발인:{' '}
                      {formatDate(order.bugo?.fevent?.deceasedInfo?.coffinOut?.date, {
                        dateSeparater: '.',
                        contains: {
                          year: false,
                          day: true,
                          hours: true,
                          minutes: true,
                        },
                      })}
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-1 whitespace-pre">
                    {order.paymentDetail?.canceledAt && (
                      <p className="text-sm text-gray-700">
                        취소:{' '}
                        {formatDate(order.paymentDetail.canceledAt, {
                          dateSeparater: '.',
                          contains: {
                            year: false,
                            day: true,
                            hours: true,
                            minutes: true,
                          },
                        })}
                      </p>
                    )}
                    {order.paymentDetail?.cancelReason && (
                      <p className="text-sm text-gray-700">
                        사유: {order.paymentDetail?.cancelReason}
                      </p>
                    )}
                    {/* 가상계좌 자동환불로 돈이 나간 계좌 — 토스가 조회로 되돌려주지 않아
                        취소 시점 기록이 유일한 근거다 ("어디로 환불됐죠?" 문의 대응) */}
                    {order.paymentDetail?.cancelAccount && (
                      <p className="col-span-2 whitespace-normal break-keep text-sm text-gray-700">
                        환불계좌: {order.paymentDetail.cancelAccount.bank}{' '}
                        {order.paymentDetail.cancelAccount.accountNumber}{' '}
                        {order.paymentDetail.cancelAccount.holderName}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <DeliveryProgressIndicator order={order} size="small" />
            <div className="flex space-x-3 pt-3">
              <DeliveryStatusManageButton shopOrder={order} />
            </div>
            <div className="flex justify-between pt-3">
              {order.deliveryDetail.deliveredImage ? (
                <div className="w-1/2">
                  <Image
                    src={fileBucketUrl(order.deliveryDetail.deliveredImage?.url, 'w200')}
                    alt="배송 완료사진"
                    preview={{
                      src: fileBucketUrl(order.deliveryDetail.deliveredImage?.url, 'raw'),
                    }}
                  />
                </div>
              ) : (
                <div></div>
              )}
              <div
                className="flex h-fit cursor-pointer gap-1 rounded-lg border border-gray-400 p-1.5"
                onClick={() => {
                  setDeliveredImageUpload(true);
                }}
              >
                <p className="font-medium">배송 완료 사진</p>
                <UpFromBracket className="wh-5 fill-gray-700" />
              </div>
            </div>
            {pendingFile && (
              <div className="mt-3 w-full rounded-lg border border-cyan-200 bg-cyan-50/40 p-3">
                <p className="mb-2 text-sm font-medium text-gray-700">
                  업로드할 배송 완료 사진
                </p>
                <div className="w-full overflow-hidden rounded-md">
                  <Image
                    src={pendingPreview}
                    alt="배송완료 미리보기"
                    className="max-h-80 w-full object-contain"
                    preview={{
                      visible: zoomVisible,
                      onVisibleChange: (v) => setZoomVisible(v),
                      src: pendingPreview,
                    }}
                  />
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <Button
                    type="button"
                    className="border border-gray-300"
                    disabled={isUploading}
                    onClick={() => setZoomVisible(true)}
                  >
                    확대보기
                  </Button>
                  <Button
                    type="button"
                    className="border border-gray-300"
                    disabled={isUploading}
                    onClick={handleQuickCancel}
                  >
                    취소
                  </Button>
                  <Button
                    type="button"
                    className="filled-gray-800"
                    disabled={isUploading}
                    onClick={handleQuickSave}
                  >
                    {isUploading ? '저장 중...' : '저장'}
                  </Button>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-4 bg-gray-50 px-5 py-3">
            {/* CS 응대 메모 — 내용까지 바로 보이고, 클릭하면 전체 타임라인·입력 */}
            <ShopOrderCsMemoBlock
              shopOrderId={order._id}
              subtitle={`보내는 분 ${deliveryDetail.senderName || '-'} · 받는 분 ${
                bareReceiverName(deliveryDetail.receiverName) || '-'
              }`}
            />
            <div className="flex items-center justify-between text-sm">
              <button
                className="font-medium text-cyan-700 hover:text-cyan-900"
                onClick={() => {
                  setDetailOpen(!detailOpen);
                }}
              >
                View all
              </button>
              <button
                className="font-medium text-cyan-700 hover:text-cyan-900"
                onClick={() => {
                  setContentOpen(!contentOpen);
                }}
              >
                발주용 내용 보기
              </button>
            </div>
            {detailOpen && (
              <div>
                <div className="py-2">
                  <p className="-mt-4 font-medium">주문번호: {order._id}</p>
                </div>
                <ShopOrderDetailInfoCard shopOrder={order} />
              </div>
            )}
            {contentOpen && (
              <div>
                <div className="flex justify-between py-2 px-2 font-medium">
                  <p>발주내용</p>
                  <div className="px-1">
                    <Button
                      className="center-box button-icon gap-0.5"
                      onClick={async (): Promise<void> => {
                        navigator.clipboard.writeText(
                          shopOrderForSellerContentString(order),
                        );
                        alert('복사 완료');
                      }}
                    >
                      <Copy className="wh-4" />
                      <p className="keep-all-preline text-sm">복사</p>
                    </Button>
                  </div>
                </div>
                <DeliveryContentCard shopOrder={order} />
              </div>
            )}
          </div>
        </div>
      </div>
      <OrderBugoDetailEditModal openId={editOpenId} order={order} />
      <OrderAddressEditModal openId={addressEditOpenId} order={order} />
      {isAdminUser && <OrderItemChangeModal openId={itemChangeOpenId} order={order} />}
    </>
  );
};
